import { useState, useContext, useEffect } from "react";
import Select from "react-select";
import { authContext } from "../contexts/AuthProvider";
import {
    getPreferredDriverOptions,
    switchOrderDriver,
} from "../services/orderService";

const SwitchDriver = ({ orderId, orderDateTime, onReRender, className = null, variant = "icon" }) => {
    const [, , merchantData] = useContext(authContext);
    const [popup, setPopup] = useState(false);
    const [driverOptions, setDriverOptions] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [now, setNow] = useState(Date.now());

    const switchWindowMinutes = merchantData?.preferredDriverSwitchWindowMinutes || 0;

    useEffect(() => {
        if (!orderDateTime || !switchWindowMinutes) return undefined;

        const placedAt = new Date(orderDateTime).getTime();
        const windowMs = switchWindowMinutes * 60 * 1000;
        const msRemaining = placedAt + windowMs - Date.now();

        if (msRemaining <= 0) return undefined;

        const timer = setInterval(() => setNow(Date.now()), 1000);

        const expiryTimeout = setTimeout(() => setNow(Date.now()), msRemaining);

        return () => {
            clearInterval(timer);
            clearTimeout(expiryTimeout);
        };
    }, [orderDateTime, switchWindowMinutes]);

    const withinWindow = () => {
        if (!orderDateTime || !switchWindowMinutes) return false;
        const placedAt = new Date(orderDateTime).getTime();
        const windowMs = switchWindowMinutes * 60 * 1000;
        return now - placedAt <= windowMs;
    };

    if (merchantData?.preferredDriverEnabled !== true || !withinWindow()) {
        return null;
    }

    const openPopup = async () => {
        setPopup(true);
        setError("");
        setIsLoading(true);
        try {
            const response = await getPreferredDriverOptions();
            if (response?.data?.code === 1) {
                setDriverOptions(response?.data?.data?.drivers ?? []);
            } else {
                setDriverOptions([]);
            }
        } catch (err) {
            setDriverOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const closePopup = () => {
        setPopup(false);
        setSelectedDriverId(null);
        setError("");
    };

    const confirmSwitch = async () => {
        if (!selectedDriverId) {
            setError("Please select a driver");
            return;
        }
        setIsSaving(true);
        setError("");
        try {
            const response = await switchOrderDriver(orderId, selectedDriverId);
            if (response?.data?.code === 1) {
                closePopup();
                onReRender?.();
            } else {
                setError(response?.data?.message ?? "Something went wrong");
            }
        } catch (err) {
            setError("Something went wrong");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            {variant === "block" ? (
                <div className="p-2 border-top border-block-start-dashed">
                    <button
                        onClick={openPopup}
                        className="btn btn-primary-light btn-wave waves-effect waves-light w-100 py-2"
                    >
                        <i className="ri-user-follow-line me-1 fw-medium align-middle"></i>
                        SWITCH DRIVER
                    </button>
                </div>
            ) : (
                <button
                    onClick={openPopup}
                    className={
                        className ??
                        `btn btn-sm btn-icon btn-primary-light btn-wave rounded-circle waves-effect waves-light`
                    }
                    title="Switch Driver"
                >
                    <i className="ri-user-follow-line"></i>
                </button>
            )}
            <div
                className={
                    popup === true
                        ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                        : "modal fade effect-slide-in-bottom"
                }
                style={popup === true ? { display: "block" } : {}}
            >
                <div
                    className="modal-dialog modal-dialog-centered modal-sm"
                    role="document"
                >
                    <div className="modal-content modal-content-demo">
                        <div className="modal-header">
                            <h6 className="modal-title">SWITCH DRIVER</h6>
                            <button
                                onClick={closePopup}
                                aria-label="Close"
                                className="btn-close"
                                data-bs-dismiss="modal"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {isLoading ? (
                                <div className="text-center my-3">
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                </div>
                            ) : (
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    placeholder="Select a driver"
                                    options={driverOptions.map((driver) => ({
                                        label: `${driver?.name ?? "Driver"} • ${driver?.vehicleType ?? ""
                                            } • ${driver?.distanceKm ?? "?"} km • ${driver?.online ? "Online" : "Offline"
                                            }${driver?.busy ? " • Busy" : ""}`,
                                        value: driver?.driverId,
                                    }))}
                                    onChange={(selected) => {
                                        setSelectedDriverId(selected?.value ?? null);
                                        setError("");
                                    }}
                                    noOptionsMessage={() =>
                                        "No drivers found in the configured zone"
                                    }
                                />
                            )}
                            {error && (
                                <small className="text-danger d-block mt-2">{error}</small>
                            )}
                        </div>
                        <div className="modal-footer">
                            <div className="d-flex justify-content-center gap-2 w-100">
                                <button
                                    type="button"
                                    onClick={closePopup}
                                    className="btn btn-danger"
                                >
                                    CANCEL
                                </button>
                                <button
                                    onClick={confirmSwitch}
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={isSaving === true}
                                >
                                    {isSaving === true ? (
                                        <span
                                            className="spinner-border spinner-border-xs"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                    ) : (
                                        <>CONFIRM</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SwitchDriver;